import { useEffect, useState } from 'react';
import { Save, RotateCcw, AlertCircle, Check, FlaskConical } from 'lucide-react';
import { pb, errorText } from '../../lib/pb';
import { useCollection } from '../../lib/hooks';
import type { RecordModel } from 'pocketbase';
import { confirmDialog } from '../ConfirmDialog';

interface SettingRecord extends RecordModel {
  key: string;
  value: unknown;
}

const GROUP_HELP: Record<string, string> = {
  costs:
    'USD per generation, keyed by model id. Easier to edit in the "Model costs" tab — this is the same data. Charged when a generation finishes (failed ones are not billed); price changes never rewrite past spend.',
  prompts:
    'Every prompt the pipeline sends. Tokens like {{characterLock}} and {{speech}} are substituted per scene. Changes apply to work queued from now on; a render already in flight finishes with the text it started with.',
  models: 'Which model each stage uses, and its output settings.',
  pipeline: 'Polling, retry limits and script QC rules.',
  subtitles: 'Caption layout per aspect ratio. fontName must be a font installed in the worker image (DejaVu Sans ships with it).',
  storage:
    'Google Drive folder IDs and retention. "logSpreadsheetId": a Google Sheet that gets one appended row per published video (Creator/Brand/Product/AI Character/Format/Date/Captioned link/Uncaptioned link/Source clips) — must be shared as editor with the service account; empty disables it.',
  rateLimits:
    'Requests per minute per provider, shared across every campaign rendering at once. Raise these only if your plan allows it.',
  notifications: 'Where to send a message when a run fails.',
};

/**
 * Settings are edited as JSON per group.
 *
 * A bespoke form per field would be a lot of surface for something changed
 * rarely, and JSON keeps every key visible — including ones added by a later
 * version. The editor validates before saving so a typo can't take the pipeline
 * down.
 */
export default function SettingsEditor() {
  const settings = useCollection<SettingRecord>('settings', { sort: 'key' });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [sheetTest, setSheetTest] = useState<'idle' | 'running' | 'ok' | 'failed'>('idle');
  const [sheetTestError, setSheetTestError] = useState('');

  // Enqueues a worker job that appends a dummy row to the publish-log sheet,
  // then polls the job for the outcome — the browser itself can't reach the
  // Sheets API (the service account lives only in the worker).
  const testSheet = async () => {
    setSheetTest('running');
    setSheetTestError('');
    try {
      const res = await fetch('/api/admin/sheet-test', {
        method: 'POST',
        headers: { Authorization: pb.authStore.token },
      });
      if (!res.ok) throw new Error(await res.text());
      const { jobId } = (await res.json()) as { jobId: string };

      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await pb.collection('jobs').getOne(jobId);
        if (job.status === 'done') {
          setSheetTest('ok');
          return;
        }
        if (job.status === 'failed') {
          setSheetTest('failed');
          setSheetTestError((job.last_error as string) || 'unknown error — see the queue');
          return;
        }
      }
      setSheetTest('failed');
      setSheetTestError('Timed out waiting for the worker — is it running? Check Admin → Queue.');
    } catch (err) {
      setSheetTest('failed');
      setSheetTestError(errorText(err));
    }
  };

  useEffect(() => {
    if (settings.loading) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const record of settings.items) {
        if (record.key === 'demo') continue; // edited in Admin → Demo assets
        // Don't clobber an edit in progress.
        if (next[record.key] === undefined) {
          next[record.key] = JSON.stringify(record.value, null, 2);
        }
      }
      return next;
    });
  }, [settings.items, settings.loading]);

  const save = async (record: SettingRecord) => {
    const raw = drafts[record.key] ?? '';
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      setErrors((e) => ({ ...e, [record.key]: `Invalid JSON — ${(err as Error).message}` }));
      return;
    }

    setBusy(record.key);
    setErrors((e) => ({ ...e, [record.key]: '' }));
    try {
      await pb.collection('settings').update(record.id, { value: parsed });
      setSaved(record.key);
      setTimeout(() => setSaved(null), 2500);
      await settings.reload();
    } catch (err) {
      setErrors((e) => ({ ...e, [record.key]: errorText(err) }));
    } finally {
      setBusy(null);
    }
  };

  const resetToDefault = async (key: string) => {
    // Read from the record, not from a bundled copy of shared/defaults.ts:
    // that file holds every prompt and the Drive folder ids, and anything
    // imported here ends up in a JS bundle served to unauthenticated visitors.
    const record = settings.items.find((r) => r.key === key);
    const fallback = (record as { default_value?: unknown } | undefined)?.default_value;
    if (fallback === undefined || fallback === null) return;
    if (
      !(await confirmDialog({
        title: `Reset "${key}"?`,
        message: 'The editor is filled with the shipped defaults. Nothing is saved until you press Save.',
        confirmLabel: 'Fill with defaults',
      }))
    ) {
      return;
    }
    setDrafts((d) => ({ ...d, [key]: JSON.stringify(fallback, null, 2) }));
  };

  if (settings.loading) {
    return <div className="zen-card p-8 text-xs text-natural-text/45">Loading settings…</div>;
  }

  return (
    <div className="space-y-4">
      {settings.items.filter((record) => record.key !== 'demo').map((record) => {
        const draft = drafts[record.key] ?? '';
        const dirty = draft !== JSON.stringify(record.value, null, 2);
        const error = errors[record.key];

        return (
          <div key={record.id} className="zen-card p-5 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-serif text-base font-medium capitalize">
                  {record.key.replace(/([A-Z])/g, ' $1')}
                </h3>
                {GROUP_HELP[record.key] && (
                  <p className="text-[11px] text-natural-text/55 mt-1 leading-relaxed max-w-2xl">
                    {GROUP_HELP[record.key]}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saved === record.key && (
                  <span className="zen-pill bg-emerald-50 text-emerald-700">
                    <Check size={11} />
                    Saved
                  </span>
                )}
                {dirty && <span className="zen-pill bg-amber-50 text-amber-700">Unsaved</span>}
              </div>
            </div>

            <textarea
              value={draft}
              onChange={(e) => setDrafts((d) => ({ ...d, [record.key]: e.target.value }))}
              spellCheck={false}
              rows={Math.min(28, Math.max(6, draft.split('\n').length + 1))}
              className="zen-input font-mono text-[11px] leading-relaxed resize-y"
            />

            {error && (
              <p className="text-xs text-red-600 flex items-start gap-1.5">
                <AlertCircle size={13} className="shrink-0 mt-0.5" />
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => void save(record)}
                disabled={!dirty || busy === record.key}
                className="zen-btn-primary py-2 px-4 text-xs"
              >
                <Save size={13} />
                {busy === record.key ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => resetToDefault(record.key)}
                className="zen-btn-ghost py-2 px-4 text-xs"
                title="Load the shipped defaults into the editor"
              >
                <RotateCcw size={13} />
                Defaults
              </button>
              {dirty && (
                <button
                  onClick={() =>
                    setDrafts((d) => ({ ...d, [record.key]: JSON.stringify(record.value, null, 2) }))
                  }
                  className="zen-btn-ghost py-2 px-4 text-xs"
                >
                  Discard
                </button>
              )}
              {record.key === 'storage' && (
                <button
                  onClick={() => void testSheet()}
                  disabled={sheetTest === 'running'}
                  className="zen-btn-ghost py-2 px-4 text-xs"
                  title="Appends one dummy row to the publish-log spreadsheet via the worker, so you can verify the id and sharing without publishing a video"
                >
                  <FlaskConical size={13} />
                  {sheetTest === 'running' ? 'Testing…' : 'Test sheet logging'}
                </button>
              )}
            </div>
            {record.key === 'storage' && sheetTest === 'ok' && (
              <p className="text-xs text-emerald-700 flex items-center gap-1.5">
                <Check size={13} />
                Dummy row appended — check the spreadsheet (and delete the row).
              </p>
            )}
            {record.key === 'storage' && sheetTest === 'failed' && (
              <p className="text-xs text-red-600 flex items-start gap-1.5">
                <AlertCircle size={13} className="shrink-0 mt-0.5" />
                Sheet test failed: {sheetTestError}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
